import { action, makeObservable, runInAction } from 'mobx';
import { destroy, get, getCount, getPage, getResponseContent, getToken, post } from 'stores/_services/dataService';
import { downloadFromPayload } from 'stores/_services/downloadService';
import { getConditions } from 'stores/_services/filterService';
import BaseListStore, { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from 'stores/BaseListStore';
import RuleStore, { IParsedFiles, IParsedFilesError, IRule } from 'stores/rule/RuleStore';
import { IDataFetchOptions } from '../../types';

const SEARCH_FIELDS = ['name', 'short'];

export default class RuleListStore extends BaseListStore<RuleStore> {
    constructor() {
        super({
            itemStore: RuleStore
        });

        makeObservable(this, {
            deleteRule: action,
            toggleEnabled: action
        });
    }

    async getRules(args: IDataFetchOptions = {}, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<void> {
        const shouldReset = this._updateListState(args);
        const conditions = getConditions(this.filter);

        const options = {
            filter: {
                searchFields: SEARCH_FIELDS,
                order: this.sort,
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                where: conditions,
                include: [
                    {
                        relation: 'author',
                        scope: {
                            fields: ['email']
                        }
                    }
                ]
            }
        };
        const { data } = await getPage('ruleDetails', options);
        const totalCount = await getCount('ruleDetails', { where: conditions, searchFields: SEARCH_FIELDS });

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, this.skip);
        // every step that updates observables in an asynchronous process
        // should be marked as action(one way to use is runInAction),
        // only synchronous changes to observable data works in actions
        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });
    }

    async getRule(ruleId: string, filtercondition = false): Promise<IRule> {
        const options = {
            filter: {
                includeDeleted: true,
                where: {
                    id: ruleId
                },
                include: [
                    {
                        relation: 'author',
                        scope: {
                            fields: ['email']
                        }
                    },
                    {
                        relation: 'editor',
                        scope: {
                            fields: ['email']
                        }
                    }
                ]
            }
        };

        const res = await get('rules', options);
        const ruleFilter = {
            filter: {
                excludeFields: ['body']
            }
        };

        const {
            data: {
                name,
                description,
                severity,
                short,
                data: { actions }
            }
        } = filtercondition ? await get(`rules/${ruleId}/latestVersion`) : await get(`rules/${ruleId}/latestVersion`, ruleFilter);

        const { data } = res;
        const rulesData = data[0];

        if (name) {
            rulesData.name = name;
            rulesData.description = description;
            rulesData.short = short;
            rulesData.severity = severity;
            rulesData.actions = actions;
        }

        this._updateData(rulesData);
        return rulesData;
    }

    async getRuleBody(ruleId: string, filtercondition = false): Promise<void> {
        const options = {
            filter: {
                excludeFields: ['body']
            }
        };

        const res = filtercondition ? await get(`rules/${ruleId}/latestVersion`) : await get(`rules/${ruleId}/latestVersion`, options);
        const { data } = res;

        this._updateData({
            id: ruleId,
            body: data.body
        });
    }

    async getRulesForExport(): Promise<IRule[]> {
        const options = {
            filter: {
                where: {
                    sourceId: null
                },
                fields: ['id', 'name']
            }
        };

        const { data } = await get('rules', options);

        return data;
    }

    async getRulePublishDate(): Promise<{ lastPublishDate: string; lastChangeDate: string }> {
        const { data } = await get('settings/getRulePublishDate');

        return data;
    }

    async saveRule(rule: IRule): Promise<void> {
        const payload = {
            id: rule.id || undefined,
            description: rule.description,
            short: rule.short,
            name: rule.name,
            body: rule.body,
            severity: rule.severity,
            mitreId: rule.mitreId,
            mitreTactic: rule.mitreTactic,
            type: rule.type || null,
            actions: { [!rule.action.length ? 'observe' : 'alert']: true }
        };

        await post('rules', payload);
    }

    async toggleEnabled(ruleId: string): Promise<void> {
        const rule = this.data.get(ruleId)?.rule as IRule;

        const payload = {
            id: ruleId,
            name: rule.name,
            active: !rule.active
        };

        await post('rules/toggleEnabled', payload);
        await this.getRule(ruleId);
    }

    async deleteRule(ruleId: string): Promise<void> {
        await destroy(`rules/${ruleId}`);
        await this.getRules({ reset: true });
    }

    async publishRules(): Promise<void> {
        await post('rules/publish');
    }

    async exportRules(ruleIds: string[]): Promise<void> {
        const { data } = await post('rules/getRulesExportPayload', { ruleIds });
        downloadFromPayload('rules', [data], 'file', 'yaml');
    }

    async uploadFiles(payload: { filename: string; file: string | ArrayBuffer | null }, preview?: boolean): Promise<IParsedFiles | IParsedFilesError> {
        const route = '/api/rules/loadFiles';

        try {
            const res = await fetch(route, {
                method: 'POST',
                headers: {
                    'Content-Type': 'binary/octet-stream',
                    filename: payload.filename,
                    preview: String(preview),
                    Authorization: getToken() || ''
                },
                body: payload.file
            });

            return await getResponseContent(route, res);
        } catch (err) {
            return {
                error: {
                    name: 'Unknown Error',
                    message: (err as Error).message
                }
            };
        }
    }
}
